import api from "@/config/api";
import { getRecordingToClean, saveRecordingToClean } from "@/storage/recording";
import { FetchErrorType } from "@/types/network";
import { clearRecordingData } from "@/utils/indexedDBUtils";
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";

const extractAxiosErrorMessage = (error: AxiosError): string => {
  const data = error.response?.data as { error?: string; message?: string } | undefined;
  return data?.error || data?.message || error.message || "Unknown error";
};

export const useFetchInterceptor = () => {
  const [error, setError] = useState<FetchErrorType | null>(null);

  const extractErrorMessage = async (response: Response): Promise<string> => {
    try {
      const data = await response.clone().json();
      return data.error || data.message || JSON.stringify(data);
    } catch {
      return response.statusText || "Unknown error";
    }
  };

  const handleError = useCallback((status: number | null, message: string | null) => {
    setError({ status, message } as FetchErrorType);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const setupFetchInterceptor = useCallback(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);

        if (response.status >= 400) {
          const message = await extractErrorMessage(response);
          handleError(response.status, message);
        } else {
          clearError();
        }

        return response;
      } catch (err: unknown) {
        if (err && typeof err === "object") {
          const status = "status" in err ? (err.status as number) : null;
          const message = "message" in err ? (err.message as string) : "Unknown error";
          handleError(status, message);
        }
        return Promise.reject(err);
      }
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [handleError, clearError]);

  const cleanRecordingFromIDB = useCallback(async () => {
    const recordingToClean = getRecordingToClean();
    if (recordingToClean) {
      await clearRecordingData(recordingToClean);
      saveRecordingToClean("");
    }
  }, []);

  useEffect(() => {
    const cleanup = setupFetchInterceptor();
    cleanRecordingFromIDB();
    return cleanup;
  }, [setupFetchInterceptor, cleanRecordingFromIDB]);

  useEffect(() => {
    api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        handleError(error.response?.status ?? null, extractAxiosErrorMessage(error));
        return Promise.reject(error);
      },
    );
  }, [handleError]);

  return { error, clearError };
};
